import { useState, useEffect } from 'react';
import apiClient from '../../../lib/api';
import { resolveImageUrl } from '../../../lib/utils';

interface Testimonial {
  id: string;
  name: string;
  location?: string;
  rating: number;
  comment: string;
  image_url?: string;
  is_featured?: number;
  is_active: number;
  created_at?: string;
  updated_at?: string;
}

const getImageUrl = (url: string | null | undefined) => {
  if (!url) return '';
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  return resolveImageUrl(url);
};

const TestimonialsManager = () => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingTestimonial, setEditingTestimonial] = useState<Testimonial | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.getTestimonials();
      setTestimonials(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load testimonials');
      console.error('Error loading testimonials:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditTestimonial = (testimonial: Testimonial) => {
    setEditingTestimonial({ ...testimonial });
    setShowModal(true);
  };

  const handleAddTestimonial = () => {
    const newTestimonial: Testimonial = {
      id: '',
      name: '',
      location: '',
      rating: 5,
      comment: '',
      image_url: '',
      is_featured: 0,
      is_active: 1
    };
    setEditingTestimonial(newTestimonial);
    setShowModal(true);
  };

  const handleSaveTestimonial = async () => {
    if (!editingTestimonial) return;

    try {
      setLoading(true); // Re-use loading state for save indicator if desired, or better add a 'saving' state

      let finalImageUrl = editingTestimonial.image_url;

      // Handle Image Upload
      if (imageFile) {
        const formData = new FormData();
        formData.append('image', imageFile);
        try {
          const uploadRes = await apiClient.uploadImage(formData, 'home'); // or 'testimonials' if supported, defaulting to home
          if (uploadRes && uploadRes.imageUrl) {
            finalImageUrl = uploadRes.imageUrl;
          }
        } catch (uploadErr) {
          console.error("Image upload failed", uploadErr);
          alert("Failed to upload image, but will try saving data.");
        }
      }

      const isEditing = testimonials.find(t => t.id === editingTestimonial.id);

      const payload = {
        name: editingTestimonial.name,
        location: editingTestimonial.location || '',
        rating: editingTestimonial.rating,
        comment: editingTestimonial.comment,
        // Ensure we don't send large base64 strings in the JSON payload
        image_url: (finalImageUrl && !finalImageUrl.startsWith('data:')) ? finalImageUrl : (editingTestimonial.image_url?.startsWith('data:') ? '' : editingTestimonial.image_url),
        is_featured: editingTestimonial.is_featured || 0
      };

      if (isEditing) {
        await apiClient.updateTestimonial(editingTestimonial.id, payload);
      } else {
        await apiClient.createTestimonial(payload);
      }

      await loadTestimonials();
      setShowModal(false);
      setEditingTestimonial(null);
      setImageFile(null); // Reset file
    } catch (error) {
      console.error('Failed to save testimonial:', error);
      setError('Failed to save testimonial');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTestimonial = async (id: string) => {
    console.log('[TestimonialsManager] handleDeleteTestimonial called with id:', id);
    const ok = window.confirm('Are you sure you want to delete this testimonial?');
    console.log('[TestimonialsManager] confirm returned:', ok);
    if (!ok) return;

    try {
      console.log('[TestimonialsManager] Proceeding with API delete for testimonial:', id);
      await apiClient.deleteTestimonial(id);
      console.log('[TestimonialsManager] Delete successful, reloading data');
      await loadTestimonials();
    } catch (error: any) {
      console.error('[TestimonialsManager] Failed to delete testimonial:', error);
      setError(error?.message || 'Failed to delete testimonial');
    }
  };

  const toggleTestimonialStatus = async (id: string) => {
    try {
      const testimonial = testimonials.find(t => t.id === id);
      if (testimonial) {
        await apiClient.updateTestimonial(id, {
          ...testimonial,
          is_active: testimonial.is_active ? 0 : 1
        });
        await loadTestimonials();
      }
    } catch (error) {
      console.error('Failed to toggle testimonial status:', error);
      setError('Failed to update testimonial status');
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const MAX = 5 * 1024 * 1024; // 5MB
      if (file.size > MAX) {
        setError('File too large. Max 5MB allowed.');
        return;
      }
      setImageFile(file); // Store file for upload

      // Preview
      const reader = new FileReader();
      reader.onload = (e) => {
        if (editingTestimonial && e.target?.result) {
          setEditingTestimonial({ ...editingTestimonial, image_url: e.target.result as string });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-400"></div>
        <span className="ml-2 text-gray-600">Loading testimonials...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">{error}</p>
        <button
          onClick={loadTestimonials}
          className="mt-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-gray-900">Testimonials Management</h3>
        <button
          onClick={handleAddTestimonial}
          className="bg-primary-400 hover:bg-primary-500 text-black px-4 py-2 rounded-lg font-semibold transition-all duration-300 flex items-center"
        >
          <i className="ri-add-line mr-2"></i>
          Add Testimonial
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {testimonials.map((testimonial) => (
          <div key={testimonial.id} className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                {[...Array(5)].map((_, i) => (
                  <i
                    key={i}
                    className={`ri-star-${i < testimonial.rating ? 'fill' : 'line'} text-yellow-400`}
                  ></i>
                ))}
              </div>
              <span className={`px-2 py-1 text-xs rounded-full ${testimonial.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                {testimonial.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>

            <div className="flex items-center space-x-4 mb-4">
              {testimonial.image_url ? (
                <img
                  src={getImageUrl(testimonial.image_url)}
                  alt={testimonial.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-gray-400"></i>
                </div>
              )}
              <div>
                <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                {testimonial.location && (
                  <p className="text-sm text-gray-600">{testimonial.location}</p>
                )}
              </div>
            </div>

            <p className="text-gray-700 text-sm mb-4 line-clamp-3">{testimonial.comment}</p>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => toggleTestimonialStatus(testimonial.id)}
                  className="text-gray-600 hover:text-gray-800 text-sm"
                >
                  <i className={testimonial.is_active ? 'ri-eye-line' : 'ri-eye-off-line'}></i>
                </button>
                <button
                  onClick={() => handleEditTestimonial(testimonial)}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  <i className="ri-edit-line"></i>
                </button>
                <button
                  onClick={() => handleDeleteTestimonial(testimonial.id)}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>

              {testimonial.is_featured ? (
                <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded">
                  Featured
                </span>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && editingTestimonial && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                {testimonials.find(t => t.id === editingTestimonial.id) ? 'Edit Testimonial' : 'Add Testimonial'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Customer Name</label>
                <input
                  type="text"
                  value={editingTestimonial.name}
                  onChange={(e) => setEditingTestimonial({ ...editingTestimonial, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="Enter customer name"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Location (Optional)</label>
                <input
                  type="text"
                  value={editingTestimonial.location || ''}
                  onChange={(e) => setEditingTestimonial({ ...editingTestimonial, location: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="City, State"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Rating</label>
                <select
                  value={editingTestimonial.rating}
                  onChange={(e) => setEditingTestimonial({ ...editingTestimonial, rating: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                >
                  {[5, 4, 3, 2, 1].map(num => (
                    <option key={num} value={num}>{num} Star{num !== 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Testimonial</label>
                <textarea
                  value={editingTestimonial.comment}
                  onChange={(e) => setEditingTestimonial({ ...editingTestimonial, comment: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  rows={4}
                  placeholder="Enter customer testimonial"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Upload Photo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                />
                <p className="text-sm text-gray-600 mt-1">or enter image URL below</p>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Image URL</label>
                <input
                  type="url"
                  value={editingTestimonial.image_url || ''}
                  onChange={(e) => setEditingTestimonial({ ...editingTestimonial, image_url: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="featured"
                  checked={!!editingTestimonial.is_featured}
                  onChange={(e) => setEditingTestimonial({ ...editingTestimonial, is_featured: e.target.checked ? 1 : 0 })}
                  className="w-4 h-4 text-primary-400 border-gray-300 rounded focus:ring-primary-400"
                />
                <label htmlFor="featured" className="text-gray-700 font-semibold">
                  Featured Testimonial
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTestimonial}
                className="px-6 py-2 bg-primary-400 hover:bg-primary-500 text-black rounded-lg font-semibold transition-all duration-300"
              >
                Save Testimonial
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialsManager;
